import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, TrendingUp } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

async function fetchRelatedPages(currentSlug, categories, primaryType) {
  // Fetch pages with similar categories or type
  const { data, error } = await supabase
    .from('seo_auto_pages')
    .select('slug, title, description, primary_type_page, categories')
    .eq('status', 'published')
    .neq('slug', currentSlug) // Exclude current page
    .or(`categories.eq.${categories},primary_type_page.eq.${primaryType}`)
    .limit(6)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Related pages fetch error:', error);
    return [];
  }

  return data || [];
}

export function RelatedPages({ currentSlug, categories, primaryType }) {
  const { data: relatedPages = [], isLoading } = useQuery({
    queryKey: ['relatedPages', currentSlug, categories, primaryType],
    queryFn: () => fetchRelatedPages(currentSlug, categories, primaryType),
    enabled: !!currentSlug,
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-20 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (!relatedPages || relatedPages.length === 0) {
    return (
      <p className="text-gray-500 dark:text-gray-400 text-sm">
        No related pages found yet. Check back soon!
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {relatedPages.map((page) => (
        <Link
          key={page.slug}
          to={`/${page.slug}`}
          className="block p-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 hover:shadow-md transition-all duration-200 group"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 dark:text-white text-sm group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-1">
                {page.title}
              </h4>
              {page.description && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                  {page.description}
                </p>
              )}
              <div className="flex items-center gap-2 mt-2">
                {page.primary_type_page && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                    {page.primary_type_page}
                  </span>
                )}
                {page.categories && page.categories !== categories && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {page.categories}
                  </span>
                )}
              </div>
            </div>
            <ExternalLink 
              className="w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 flex-shrink-0 transition-colors" 
              aria-hidden="true"
            />
          </div>
        </Link>
      ))}
      
      {relatedPages.length >= 6 && (
        <Link
          to="/pages"
          className="block text-center py-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
        >
          View All Pages →
        </Link>
      )}
    </div>
  );
}
